import type { Accomplishment } from '../../stores/libraryStore'

interface AccomplishmentCardProps {
  accomplishment: Accomplishment
  onView: () => void
  onEdit: () => void
  onFramings: () => void
  onArchive: () => void
  onReview?: (action: 'confirm' | 'discard') => void
  onReviewEdit?: () => void
  onRestore?: () => void
  isNew?: boolean
}

const FACT_LABELS: Record<string, string> = {
  outcome_metric: 'Outcome',
  timeline_months: 'Timeline',
  team_size: 'Team size',
  scope_description: 'Scope',
  initiative_origin: 'Initiative',
}

function formatFactValue(key: string, value: unknown): string {
  if (key === 'timeline_months' && typeof value === 'number') return `${value} months`
  if (key === 'team_size' && typeof value === 'number') return `${value} people`
  return String(value)
}

export default function AccomplishmentCard({
  accomplishment,
  onView,
  onEdit,
  onFramings,
  onArchive,
  onReview,
  onReviewEdit,
  onRestore,
  isNew = false,
}: AccomplishmentCardProps) {
  const formatTag = (term: string) =>
    term.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())

  const isNeedsReview = accomplishment.extraction_status === 'needs_review'
  const isArchived = accomplishment.extraction_status === 'archived'
  const isQaEnrichment = accomplishment.source === 'qa_enrichment' && isNeedsReview
  const qaOrigin = accomplishment.qa_origin
  const displayFacts = accomplishment.display_facts

  return (
    <div className="rounded-lg border border-stone-200 bg-white p-4 transition-shadow hover:shadow-sm">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium text-stone-800 text-sm">{accomplishment.employer}</span>
            <span className="text-stone-300">&middot;</span>
            <span className="text-stone-600 text-sm">{accomplishment.role_title}</span>
            {accomplishment.period && (
              <>
                <span className="text-stone-300">&middot;</span>
                <span className="text-stone-400 text-sm">{accomplishment.period}</span>
              </>
            )}
            {isNew && (
              <span className="rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700">
                New
              </span>
            )}
            {isNeedsReview && (
              <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700">
                Needs Review
              </span>
            )}
            {isArchived && (
              <span className="rounded-full bg-stone-100 px-2 py-0.5 text-xs font-medium text-stone-500">
                Archived
              </span>
            )}
            {accomplishment.source === 'manual' && (
              <span className="rounded-full bg-blue-50 px-2 py-0.5 text-xs text-blue-600">
                Manual
              </span>
            )}
          </div>

          {accomplishment.competency_tags.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {accomplishment.competency_tags.map((tag) => (
                <span
                  key={tag}
                  className="rounded-full bg-stone-50 px-2 py-0.5 text-xs text-stone-500"
                >
                  {formatTag(tag)}
                </span>
              ))}
            </div>
          )}

          {isQaEnrichment && qaOrigin && (
            <div className="mt-3 rounded border border-amber-100 bg-amber-50 px-3 py-2 text-xs text-stone-600">
              {qaOrigin.question && (
                <p className="text-stone-400 mb-1 truncate">{qaOrigin.question}</p>
              )}
              <p className="text-stone-700 leading-relaxed">
                &ldquo;{qaOrigin.answer_snippet.length > 150
                  ? qaOrigin.answer_snippet.slice(0, 150) + '…'
                  : qaOrigin.answer_snippet}&rdquo;
              </p>
              {displayFacts && Object.keys(displayFacts).length > 0 && (
                <ul className="mt-2 space-y-0.5">
                  {Object.entries(displayFacts).map(([k, v]) => (
                    <li key={k} className="flex gap-1.5">
                      <span className="text-stone-400 shrink-0">{FACT_LABELS[k] ?? k} ·</span>
                      <span>{formatFactValue(k, v)}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>

        <div className="flex items-center gap-1 shrink-0">
          {isQaEnrichment ? (
            <>
              <button
                onClick={onReviewEdit}
                className="rounded px-2.5 py-1 text-xs font-medium text-stone-700 border border-stone-200 hover:bg-stone-50 transition-colors"
              >
                Review &amp; Add
              </button>
              <button
                onClick={() => onReview?.('discard')}
                className="rounded px-2.5 py-1 text-xs font-medium text-red-600 hover:bg-red-50 transition-colors"
              >
                Discard
              </button>
            </>
          ) : isNeedsReview && onReview ? (
            <>
              <button
                onClick={() => onReview('confirm')}
                className="rounded px-2.5 py-1 text-xs font-medium text-green-700 hover:bg-green-50 transition-colors"
              >
                Confirm
              </button>
              <button
                onClick={() => onReview('discard')}
                className="rounded px-2.5 py-1 text-xs font-medium text-red-600 hover:bg-red-50 transition-colors"
              >
                Discard
              </button>
            </>
          ) : isArchived && onRestore ? (
            <button
              onClick={onRestore}
              className="rounded px-2.5 py-1 text-xs font-medium text-stone-600 hover:bg-stone-50 transition-colors"
            >
              Restore
            </button>
          ) : (
            <>
              <button
                onClick={onView}
                className="rounded px-2.5 py-1 text-xs text-stone-600 hover:bg-stone-50 transition-colors"
              >
                View
              </button>
              <button
                onClick={onEdit}
                className="rounded px-2.5 py-1 text-xs text-stone-600 hover:bg-stone-50 transition-colors"
              >
                Edit
              </button>
              <button
                onClick={onFramings}
                className="rounded px-2.5 py-1 text-xs text-stone-600 hover:bg-stone-50 transition-colors"
              >
                Perspectives
              </button>
              <button
                onClick={onArchive}
                className="rounded px-2.5 py-1 text-xs text-red-500 hover:bg-red-50 transition-colors"
              >
                Archive
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
